import {
  MediaSession as MediaSessionPlugin,
  type MetadataOptions,
  type MediaSessionAction,
  type ActionHandler,
  type PositionStateOptions,
} from '@capgo/capacitor-media-session';
import { Capacitor } from '@capacitor/core';

export type MediaSessionMetadata = {
  title: string;
  artist?: string;
  coverUrl?: string;
};

const getFileTypeFromCoverUrl = (coverUrl: string) => {
  const cleanCoverUrl = coverUrl.split('?')[0]; //Remove query parameters if there are any
  const fileExtension = cleanCoverUrl.split('.').pop();
  return fileExtension ? fileExtension.toLowerCase() : '';
};

/**
 * To support background playback on both iOS and Android we need to use both the Media Session API (which supports iOS & Web)
 * and the Capgo media session plugin, as the plugin is currently only working for Android.
 */
const useMediaSession = () => {
  const isAndroid = Capacitor.getPlatform() === 'android';
  const hasWebMediaSession = 'mediaSession' in navigator;
  const useMediaSessionAPI = !isAndroid && hasWebMediaSession;
  const isMediaSessionSupported = isAndroid || useMediaSessionAPI;

  const artworkSizes = ['96x96', '128x128', '192x192', '256x256', '384x384', '512x512'];

  /**
   * Register a handler for a given Media Session action.
   * @param action - A media session action (e.g. 'play', 'pause', etc.).
   * @param handler - The function to call when the given action is triggered.
   */
  const registerActionHandler = async (action: MediaSessionAction, handler: ActionHandler | null) => {
    if (!isMediaSessionSupported) return;
    try {
      if (useMediaSessionAPI) {
        /**
         * Note: 'ActionDetails' (part of ActionHandler) is a structural subset of 'MediaSessionActionDetails'
         * (part of MediaSessionActionHandler). The handler only consumes shared properties (action, seekTime) and
         * safely ignores browser-specific extras like fastSeek or seekOffset.
         */
        navigator.mediaSession.setActionHandler(action, handler as MediaSessionActionHandler);
        return;
      }

      await MediaSessionPlugin.setActionHandler({ action }, handler);
    } catch (e) {
      console.warn(`Failed to register Media Session action "${action}" `, e);
    }
  };

  const registerPlayHandler = async (fn: ActionHandler | null) => {
    await registerActionHandler('play', fn);
  };

  const registerPauseHandler = async (fn: ActionHandler | null) => {
    await registerActionHandler('pause', fn);
  };

  const registerNextTrackHandler = async (fn: ActionHandler | null) => {
    await registerActionHandler('nexttrack', fn);
  };

  const registerPreviousTrackHandler = async (fn: ActionHandler | null) => {
    await registerActionHandler('previoustrack', fn);
  };

  const registerSeekToHandler = async (fn: ActionHandler | null) => {
    await registerActionHandler('seekto', fn);
  };

  const registerSeekForwardHandler = async (fn: ActionHandler | null) => {
    await registerActionHandler('seekforward', fn);
  };

  const registerSeekBackwardHandler = async (fn: ActionHandler | null) => {
    await registerActionHandler('seekbackward', fn);
  };

  const setPlayBackState = async (isPlaying: boolean) => {
    try {
      if (!isMediaSessionSupported) return;

      const playBackState = isPlaying ? 'playing' : 'paused';

      if (useMediaSessionAPI) {
        navigator.mediaSession.playbackState = playBackState;
        return;
      }

      await MediaSessionPlugin.setPlaybackState({ playbackState: playBackState });
    } catch (e) {
      console.warn('Failed to set playback state', e);
    }
  };

  const setMediaMetadata = async (metadata: MediaSessionMetadata) => {
    try {
      if (!isMediaSessionSupported) return;

      let metadataOptions: MetadataOptions = { title: metadata.title, artist: metadata.artist };

      if (metadata.coverUrl) {
        const coverUrlType = getFileTypeFromCoverUrl(metadata.coverUrl);

        const artWorks: MediaImage[] = artworkSizes.map((size) => ({
          src: metadata.coverUrl!,
          sizes: size,
          type: `image/${coverUrlType}`,
        }));

        metadataOptions = {
          ...metadataOptions,
          artwork: artWorks,
        };
      }

      if (useMediaSessionAPI) {
        navigator.mediaSession.metadata = new MediaMetadata(metadataOptions);
        return;
      }

      await MediaSessionPlugin.setMetadata(metadataOptions);
    } catch (e) {
      console.warn('Failed to set media session metadata', e);
    }
  };

  const setPositionState = async (audioElement: HTMLAudioElement | null) => {
    try {
      if (!audioElement || !isMediaSessionSupported) return;

      const { duration, playbackRate, currentTime } = audioElement;
      if (!Number.isFinite(duration) || duration <= 0) return;

      //Current position should never exceed the duration.
      const position = Math.min(currentTime, duration);

      const positionStateOptions: PositionStateOptions = {
        duration,
        playbackRate,
        position,
      };

      if (useMediaSessionAPI) {
        navigator.mediaSession.setPositionState(positionStateOptions);
        return;
      }

      await MediaSessionPlugin.setPositionState(positionStateOptions);
    } catch (e) {
      console.warn('Failed to set position state:', e);
    }
  };

  const clearMediaSession = async () => {
    try {
      if (!isMediaSessionSupported) return;

      // Unregister all action handlers
      await registerPlayHandler(null);
      await registerPauseHandler(null);
      await registerNextTrackHandler(null);
      await registerPreviousTrackHandler(null);
      await registerSeekToHandler(null);
      await registerSeekForwardHandler(null);
      await registerSeekBackwardHandler(null);

      //Clear metadata
      if (useMediaSessionAPI) {
        navigator.mediaSession.metadata = null;
        return;
      }

      await MediaSessionPlugin.setMetadata({ artist: '', title: '', artwork: [], album: '' });
    } catch (e) {
      console.warn('Failed to clear media session metadata', e);
    }
  };

  return {
    registerPlayHandler,
    registerPauseHandler,
    registerNextTrackHandler,
    registerPreviousTrackHandler,
    registerSeekToHandler,
    registerSeekForwardHandler,
    registerSeekBackwardHandler,
    setMediaMetadata,
    clearMediaSession,
    getFileTypeFromCoverUrl,
    setPlayBackState,
    setPositionState,
  };
};

export default useMediaSession;
